import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { listThreadPlacements, orderPinnedThreads, placeThread, type PersonPlacementEvent, type ThreadPlacement } from "../../../api/threads/placementsApi";
import { THREAD_LIST_LIMIT } from "../../../api/threads/threadsApi";
import { reportUiError, toUiError, type UiError } from "../../../errors/uiError";
import { useLoad } from "../../../shared/loading/useLoad";
import { writeStoredString } from "../../../shared/storage/storedString";
import { inboxPlace, nextWakeAt } from "../list/inboxPlacement";
import type { DropPlan } from "../list/threadDrops";
import { useLiveProject } from "../live/threadLiveContext";

export interface ThreadPlacements {
  /** The person's placed (pinned, settled or snoozed) Threads by ID; empty until loaded. */
  readonly byThread: ReadonlyMap<string, ThreadPlacement>;
  /** The time the list's places are read at: it moves on when a snooze ends. */
  readonly now: number;
  readonly place: (threadId: string, event: PersonPlacementEvent, snoozedUntil?: Date) => void;
  /** A drop in the sidebar: its changes one after the other, then the new pin order; one refresh. */
  readonly drop: (plan: DropPlan) => void;
  /** The last placement write that failed, until the next one succeeds. */
  readonly error: UiError | null;
}

const changedStorageKeyOf = (projectId: string) => `dmw.threadPlacementsChanged.${projectId}`;

/**
 * The person's own settled and snoozed Threads in the project (F44). A change here is told to
 * the split view's other pane through storage, as the corpus choice is; the clock moves on at
 * the next snooze's end, so a Thread wakes on time without a reload. Module C says on the live
 * socket when the person's placements changed, a snooze it woke among them; they are read
 * again then (the project feed's `placementsChanged`).
 */
export function useThreadPlacements(projectId: string): ThreadPlacements {
  const load = useCallback((signal: AbortSignal) => listThreadPlacements(projectId, signal), [projectId]);
  const { state, refresh } = useLoad(load);
  const [now, setNow] = useState(() => Date.now());
  const [error, setError] = useState<UiError | null>(null);
  const byThread = useMemo(
    () => new Map(state.status === "loaded" ? state.value.map((placement) => [placement.threadId, placement] as const) : []),
    [state],
  );

  const placementsChanged = useLiveProject(projectId, THREAD_LIST_LIMIT)?.placementsChanged ?? 0;
  const [readChange, setReadChange] = useState(placementsChanged);
  if (readChange !== placementsChanged) {
    setReadChange(placementsChanged);
    refresh();
  }

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === changedStorageKeyOf(projectId)) refresh();
    };
    window.addEventListener("storage", onStorage);
    return () => { window.removeEventListener("storage", onStorage); };
  }, [projectId, refresh]);

  const wakeAt = nextWakeAt(byThread.values(), now);
  useEffect(() => {
    if (wakeAt === null) return undefined;
    const timer = window.setTimeout(() => { setNow(Date.now()); }, Math.max(0, wakeAt - Date.now()));
    return () => { window.clearTimeout(timer); };
  }, [wakeAt]);

  const write = useCallback(
    (writes: () => Promise<unknown>) => {
      writes().then(
        () => {
          setError(null);
          setNow(Date.now());
          refresh();
          writeStoredString(changedStorageKeyOf(projectId), new Date().toISOString());
        },
        (caught: unknown) => {
          const failure = toUiError(caught);
          reportUiError(failure);
          setError(failure);
        },
      );
    },
    [projectId, refresh],
  );
  const place = useCallback(
    (threadId: string, event: PersonPlacementEvent, snoozedUntil?: Date) => {
      write(() => placeThread(threadId, event, snoozedUntil?.toISOString() ?? null));
    },
    [write],
  );
  const drop = useCallback(
    (plan: DropPlan) => {
      if (plan.changes.length === 0 && plan.pinOrder === null) return;
      write(async () => {
        for (const change of plan.changes) await placeThread(change.threadId, change.event);
        if (plan.pinOrder !== null) await orderPinnedThreads(projectId, plan.pinOrder);
      });
    },
    [projectId, write],
  );

  return { byThread, now, place, drop, error };
}

/**
 * Opening a Thread ends its Woke badge (Johannes, 27 Oct: "Woke stays until opened"), by any
 * path: the sidebar, the Threads page, a search hit or a link (Sol S5). Once per opening.
 */
export function useAcknowledgeWoke(projectId: string, threadId: string): void {
  const placements = useThreadPlacements(projectId);
  const woke = inboxPlace(placements.byThread.get(threadId), placements.now) === "woke";
  const { place } = placements;
  const acknowledged = useRef<string | null>(null);
  useEffect(() => {
    if (woke && acknowledged.current !== threadId) {
      acknowledged.current = threadId;
      place(threadId, "wake");
    }
  }, [woke, threadId, place]);
}
